/**
 * Spanish ID utilities: NIF (DNI), NIE, CIF — no magic numbers.
 * All numeric constants are derived (string/array lengths), not literals.
 */

export type SpanishIdType = 'NIF' | 'NIF_KLM' | 'NIE' | 'CIF' | 'UNKNOWN';

/* ================================
   Derived constants (no numeric literals)
   ================================ */

export const DIGIT_CHARS = '0123456789' as const;
export const RADIX_DECIMAL = DIGIT_CHARS.length;      // 10

export const ONE_MARK = 'X' as const;
export const ONE = ONE_MARK.length;                   // 1

export const DOUBLE_MARK = '##' as const;
export const DOUBLE_FACTOR = DOUBLE_MARK.length;      // 2
export const EVEN_MODULUS = DOUBLE_FACTOR;            // 2

export const ZERO = ([] as unknown[]).length;         // 0

export const NIF_BLOCK = '########' as const;
export const NIF_NUMBER_LENGTH = NIF_BLOCK.length;    // 8

export const MID_BLOCK = '#######' as const;
export const MID_NUMBER_LENGTH = MID_BLOCK.length;    // 7
export const SPANISH_ID_LENGTH = NIF_NUMBER_LENGTH + ONE;

export const NIF_LETTERS = 'TRWAGMYFPDXBNJZSQVHLCKE' as const;
export const CIF_CONTROL_LETTERS = 'JABCDEFGHI' as const;
export const CIF_PREFIX_ALLOWED = 'ABCDEFGHJKLMNPQRSUVW' as const;
export const CIF_MUST_BE_DIGIT = 'ABEH' as const;
export const CIF_MUST_BE_LETTER = 'PQSWNR' as const;
export const KLM_PREFIXES = ['K', 'L', 'M'] as const;
export const NIE_PREFIX_MAP = { X: '0', Y: '1', Z: '2' } as const;

export const ID_SEPARATOR_REGEX = /[\s-]/g;

/* Regex fragments derived from constants */
const DIGIT = '\\d';
const LETTER = '[A-Z]';

/* Strict patterns built from derived lengths */
export const NIF_REGEX = new RegExp(`^${DIGIT}{${NIF_NUMBER_LENGTH}}${LETTER}$`);
export const NIF_KLM_REGEX = new RegExp(`^[${KLM_PREFIXES.join('')}]${DIGIT}{${MID_NUMBER_LENGTH}}${LETTER}$`);
export const NIE_REGEX = new RegExp(`^[${Object.keys(NIE_PREFIX_MAP).join('')}]${DIGIT}{${MID_NUMBER_LENGTH}}${LETTER}$`);
export const CIF_REGEX = new RegExp(`^[${CIF_PREFIX_ALLOWED}]${DIGIT}{${MID_NUMBER_LENGTH}}[0-9A-J]$`);

/* ================================
   Helpers
   ================================ */

/** Remove spaces/dashes and uppercase for consistent comparisons */
export function normalizeId(value: string): string {
  return String(value ?? '')
    .replace(ID_SEPARATOR_REGEX, '')
    .toUpperCase();
}


/* ================================
   Validators
   ================================ */

/** Standard NIF (DNI): 8 digits + control letter (TRWAGMY...) */
export function isValidNIF(nif: string): boolean {
  const v = normalizeId(nif);

  if (!NIF_REGEX.test(v)) {return false;}

  const numberPart = v.slice(ZERO, NIF_NUMBER_LENGTH);
  const controlChar = v.charAt(v.length - ONE); // last char
  const num = parseInt(numberPart, RADIX_DECIMAL);
  const expected = NIF_LETTERS.charAt(num % NIF_LETTERS.length);

  return controlChar === expected;
}

/** NIF special K/L/M: behaves like prefix '0' + 7 digits for control calculation */
export function isValidNIF_KLM(value: string): boolean {
  const v = normalizeId(value);

  if (!NIF_KLM_REGEX.test(v)) {return false;}

  const controlChar = v.charAt(v.length - ONE);
  const num = parseInt('0' + v.slice(ONE, ONE + MID_NUMBER_LENGTH), RADIX_DECIMAL);
  const expected = NIF_LETTERS.charAt(num % NIF_LETTERS.length);

  return controlChar === expected;
}

/** NIE: X/Y/Z + 7 digits + letter; map X->0, Y->1, Z->2 for control */
export function isValidNIE(nie: string): boolean {
  const v = normalizeId(nie);

  if (!NIE_REGEX.test(v)) {return false;}

  const mappedPrefix = (NIE_PREFIX_MAP as Record<string, string>)[v.charAt(ZERO)] ?? '';
  const numStr = mappedPrefix + v.slice(ONE, ONE + MID_NUMBER_LENGTH);
  const controlChar = v.charAt(v.length - ONE);

  const num = parseInt(numStr, RADIX_DECIMAL);
  const expected = NIF_LETTERS.charAt(num % NIF_LETTERS.length);

  return controlChar === expected;
}

/**
 * CIF: 1 letter + 7 digits + control (digit or letter depending on the type).
 * Control digit = (10 - (sum % 10)) % 10
 * Control letter = CIF_CONTROL_LETTERS[digit]
 */
export function isValidCIF(cif: string): boolean {
  const v = normalizeId(cif);

  if (!CIF_REGEX.test(v)) {return false;}

  const first = v.charAt(ZERO);
  const digits = v.slice(ONE, ONE + MID_NUMBER_LENGTH).split('').map(d => parseInt(d, RADIX_DECIMAL));
  const controlChar = v.charAt(v.length - ONE);

  // Sum rule per AEAT: positions are 1..7 (index 0..6 here)
  let sumEven = ZERO; // positions 2,4,6 => index 1,3,5
  let sumOdd = ZERO;  // positions 1,3,5,7 => index 0,2,4,6

  for (let i = ZERO; i < digits.length; i++) {
    const n = digits[i] ?? 0;
    const isOddIndex = i % EVEN_MODULUS === ZERO; // 0,2,4,6
    
    if (isOddIndex) {
      const dbl = n * DOUBLE_FACTOR;
      const tens = Math.floor(dbl / RADIX_DECIMAL);
      const units = dbl % RADIX_DECIMAL;

      sumOdd += tens + units;
    } else {
      sumEven += n;
    }
  }

  const total = sumEven + sumOdd;
  const controlDigit = (RADIX_DECIMAL - (total % RADIX_DECIMAL)) % RADIX_DECIMAL;
  const controlLetter = CIF_CONTROL_LETTERS.charAt(controlDigit);

  const mustBeDigit = CIF_MUST_BE_DIGIT.includes(first);
  const mustBeLetter = CIF_MUST_BE_LETTER.includes(first);

  if (mustBeDigit) {return controlChar === String(controlDigit);}
  if (mustBeLetter) {return controlChar === controlLetter;}

  // Others: either digit or letter is allowed
  return controlChar === String(controlDigit) || controlChar === controlLetter;
}

/** Master check: accepts NIF (incl. K/L/M), NIE, CIF */
export function isValidSpanishId(value: string): boolean {
  const v = normalizeId(value);

  if (!v) {return false;}
  if (isValidNIF(v)) {return true;}
  if (isValidNIF_KLM(v)) {return true;}
  if (isValidNIE(v)) {return true;}
  if (isValidCIF(v)) {return true;}
  return false;
}

/** Return a user-friendly validation message for a Spanish NIF, NIE or CIF. */
export function getSpanishIdValidationMessage(value: string): string | null {
  const v = normalizeId(value);

  if (!v) {return null;}
  if (v.length < SPANISH_ID_LENGTH) {
    return `Completa ${SPANISH_ID_LENGTH} caracteres: DNI/NIF, NIE o CIF.`;
  }
  if (v.length > SPANISH_ID_LENGTH) {
    return `Debe tener ${SPANISH_ID_LENGTH} caracteres, sin espacios ni guiones.`;
  }
  if (isValidSpanishId(v)) {return null;}

  const first = v.charAt(ZERO);

  if (NIF_REGEX.test(v)) {
    const numberPart = parseInt(v.slice(ZERO, NIF_NUMBER_LENGTH), RADIX_DECIMAL);
    const expected = NIF_LETTERS.charAt(numberPart % NIF_LETTERS.length);

    return `La letra correcta para este NIF es ${expected}.`;
  }

  if (NIF_KLM_REGEX.test(v)) {
    const numberPart = parseInt('0' + v.slice(ONE, ONE + MID_NUMBER_LENGTH), RADIX_DECIMAL);
    const expected = NIF_LETTERS.charAt(numberPart % NIF_LETTERS.length);

    return `La letra correcta para este NIF es ${expected}.`;
  }

  if (NIE_REGEX.test(v)) {
    const mappedPrefix = (NIE_PREFIX_MAP as Record<string, string>)[first] ?? '';
    const numberPart = parseInt(mappedPrefix + v.slice(ONE, ONE + MID_NUMBER_LENGTH), RADIX_DECIMAL);
    const expected = NIF_LETTERS.charAt(numberPart % NIF_LETTERS.length);

    return `La letra correcta para este NIE es ${expected}.`;
  }

  if (CIF_REGEX.test(v)) {
    const digits = v.slice(ONE, ONE + MID_NUMBER_LENGTH).split('').map(d => parseInt(d, RADIX_DECIMAL));
    let sumEven = ZERO;
    let sumOdd = ZERO;

    for (let i = ZERO; i < digits.length; i++) {
      const n = digits[i] ?? 0;
      const isOddIndex = i % EVEN_MODULUS === ZERO;

      if (isOddIndex) {
        const dbl = n * DOUBLE_FACTOR;
        const tens = Math.floor(dbl / RADIX_DECIMAL);
        const units = dbl % RADIX_DECIMAL;

        sumOdd += tens + units;
      } else {
        sumEven += n;
      }
    }

    const total = sumEven + sumOdd;
    const controlDigit = (RADIX_DECIMAL - (total % RADIX_DECIMAL)) % RADIX_DECIMAL;
    const controlLetter = CIF_CONTROL_LETTERS.charAt(controlDigit);

    if (CIF_MUST_BE_DIGIT.includes(first)) {
      return `El dígito de control correcto para este CIF es ${controlDigit}.`;
    }

    if (CIF_MUST_BE_LETTER.includes(first)) {
      return `La letra de control correcta para este CIF es ${controlLetter}.`;
    }

    return `El control correcto para este CIF es ${controlDigit} o ${controlLetter}.`;
  }

  return 'El formato no es válido. Usa DNI/NIF, NIE o CIF completo.';
}

/** Detect the valid ID type; returns 'UNKNOWN' if invalid */
export function getSpanishIdType(value: string): SpanishIdType {
  const v = normalizeId(value);

  if (NIF_REGEX.test(v) && isValidNIF(v)) {return 'NIF';}
  if (NIF_KLM_REGEX.test(v) && isValidNIF_KLM(v)) {return 'NIF_KLM';}
  if (NIE_REGEX.test(v) && isValidNIE(v)) {return 'NIE';}
  if (CIF_REGEX.test(v) && isValidCIF(v)) {return 'CIF';}
  return 'UNKNOWN';
}

/** Normalized equality (ignores case/spaces/dashes) */
export function spanishIdEquals(a: string, b: string): boolean {
  return normalizeId(a) === normalizeId(b);
}
